import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {CounselingInductionService} from "../counseling-induction.service";
import {ActivatedRoute} from "@angular/router";
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {MatDialog} from "@angular/material/dialog";
import {EmployeeService} from "../../../../../services/employee.service";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-counselling-induction-table',
  templateUrl: './counselling-induction-table.component.html',
  styleUrls: ['./counselling-induction-table.component.scss']
})
export class CounsellingInductionTableComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();

  tableForm!: FormGroup;
  momId: string | null = null;
  loading = true;
  actionLoading = false;
  counsellors$: Observable<any[]>;
  dialogTitle = 'Add New Row';

  displayedColumns: string[] = [
    'srNo',
    'topic',
    'inductor',
    'date',
    'subTopic',
    'description',
    'feedback',
    'conductedBy',
    'actions',
  ];

  @ViewChild('addRowDialog') addRowDialog!: TemplateRef<any>;
  @ViewChild('feedbackDialog') feedbackDialog!: TemplateRef<any>;

  addRowForm = this.fb.group({
    topic: ['', Validators.required],
    inductorBy: [''],
    date: [''],
    subTopic: [''],
    description: [''],
    feedback: [''],
    conductedBy: ['']
  });

  editingRowIndex: number | null = null;

  constructor(
    private fb: FormBuilder,
    private inductionService: CounselingInductionService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private readonly mEmployeeService: EmployeeService,
  ) {
    this.counsellors$ = this.mEmployeeService.getCounsellers();
  }


  ngOnInit(): void {
    // Initialize form
    this.tableForm = this.fb.group({
      rows: this.fb.array([])
    });

    // Check if editing
    this.momId = this.route.snapshot.paramMap.get('id');

    if (this.momId) {
      this.loadExistingData(this.momId);
    } else {
      this.loadList();
    }
  }

  get rows(): FormArray {
    return this.tableForm.get('rows') as FormArray;
  }

  createRow(data: any): FormGroup {
    return this.fb.group({
      topic: [data.topic || ''],
      inductorBy: [data.inductorBy || ''],
      date: [ data.date?.toDate ? data.date.toDate() : data.date,],
      subTopic: [data.subTopic || ''],
      description: [data.description || ''],
      feedback: [data.feedback || ''],
      inductorFor: [data.inductorFor || '']
    });
  }

  loadList() {
    this.loading = true;

    this.inductionService.getAll().pipe(takeUntil(this._destroyed$)).subscribe({
      next: (data: any[]) => {
        console.log('API Response:', data);

        this.rows.clear();

        if (data && data.length > 0) {
          const firstItem = data[0];

          // ⭐ FIX: Set Document ID here
          this.momId = firstItem.id;
          console.log('Extracted Document ID:', this.momId);

          if (firstItem.rows && Array.isArray(firstItem.rows)) {
            firstItem.rows.forEach((row: any) => {
              this.rows.push(this.createRow(row));
            });
          }
        }

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.loading = false;
      }
    });
  }


  loadExistingData(id: string) {
    this.loading = true;

    this.inductionService.getDataById(id).pipe(takeUntil(this._destroyed$)).subscribe({
      next: (data) => {
        console.log('Loaded existing data:', data);

        // Clear existing rows
        this.rows.clear();

        if (data && data.rows && Array.isArray(data.rows) && data.rows.length > 0) {
          data.rows.forEach((row: any) => {
            this.rows.push(this.createRow(row));
          });
        } else {
          console.warn('No rows found for ID:', id);
        }

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading existing data:', error);
        this.loading = false;
      }
    });
  }

  openAddRowDialog() {
    this.editingRowIndex = null;
    this.dialogTitle = 'Add New Row';

    this.addRowForm.reset();
    this.dialog.open(this.addRowDialog);
  }


  submitRow() {
    if (this.addRowForm.invalid) return;
    if (!this.momId) return;

    this.actionLoading = true;
    const formValue = this.addRowForm.value;

    if (this.editingRowIndex !== null) {
      // --------- EDIT MODE ---------
      this.rows.at(this.editingRowIndex).patchValue(formValue);
      this.editingRowIndex = null;
    } else {
      // --------- ADD MODE ---------
      this.rows.push(this.createRow(formValue));
    }

    const updatedRows = this.rows.value;

    this.inductionService.updateRows(this.momId, updatedRows)
      .then(() => {
        this.dialog.closeAll();
        this.loadList();
      })
      .finally(() => {
        this.actionLoading = false;
      });
  }


  openEditRowDialog(index: number) {
    this.editingRowIndex = index;
    this.dialogTitle = 'Update Row';

    const currentRow = this.rows.at(index).value;

    this.addRowForm.patchValue({
      ...currentRow,
      date: currentRow.date ? new Date(currentRow.date) : null
    });

    this.dialog.open(this.addRowDialog);
  }


  deleteRow(index: number) {
    if (!this.momId) return;

    const confirmDelete = confirm("Are you sure you want to delete this row?");
    if (!confirmDelete) return;

    this.rows.removeAt(index);

    const updatedRows = this.rows.value;

    this.inductionService.updateRows(this.momId, updatedRows)
      .then(() => {
        console.log("Row deleted");
        this.loadList();
      });
  }



  protected readonly PermissionType = PermissionType;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
